import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

/**
 * Xuất Excel tổng hợp phiếu thu (Dành cho màn hình chính StudentInvoiceSummary):
 * - Mỗi học sinh là 1 Sheet riêng biệt.
 * - Trong mỗi sheet, mỗi phiếu thu được tách thành một khối/bảng riêng biệt
 *   với đầy đủ các khoản thu chi tiết (`invoiceDetails`), thông tin đợt, trạng thái và tổng tiền.
 * 
 * @param {Object} params
 * @param {Array} params.invoices Danh sách các phiếu thu trả về từ API web/receipt/v1/invoice/student-summary/export
 * @param {String} params.title Tiêu đề chính
 */
export async function exportStudentInvoicesMultiSheetExcel({
  invoices = [],
  title = "DANH SÁCH PHIẾU THU HỌC SINH",
}) {
  if (!Array.isArray(invoices) || invoices.length === 0) {
    throw new Error("Không có dữ liệu phiếu thu để xuất Excel");
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OneKids";
  workbook.lastModifiedBy = "OneKids";
  workbook.created = new Date();
  workbook.modified = new Date();

  // Helper mappings
  const typeMap = { CLASS: "Lớp", GROUP: "Nhóm", PACKAGE: "Gói" };
  const statusMap = {
    UNBILLED: "Chưa có phiếu thu",
    UNPAID: "Chưa đóng 100%",
    PARTIALLY_PAID: "Chưa đóng đủ",
    FULLY_PAID: "Đóng đủ 100%",
    OVERPAID: "Đóng thừa",
    FULLY_PAID_WITH_SURPLUS: "Đóng đủ thừa",
    PAID_WITH_DEFICIT: "Đóng đủ thiếu",
    HIDE: "Ẩn phiếu thu",
  };

  const formatDate = (v) => {
    if (!v) return "—";
    const parts = String(v).split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return v;
  };

  const formatMoney = (val) => Number(val || 0).toLocaleString("vi-VN");

  const borderThin = {
    top: { style: "thin", color: { argb: "FFCBD5E1" } },
    left: { style: "thin", color: { argb: "FFCBD5E1" } },
    bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
    right: { style: "thin", color: { argb: "FFCBD5E1" } },
  };

  // 1. Nhóm phiếu thu theo từng học sinh
  const studentGroups = new Map();
  invoices.forEach((inv) => {
    const key = inv.idStudent || inv.studentCode || inv.fullName || "default";
    if (!studentGroups.has(key)) {
      studentGroups.set(key, {
        idStudent: inv.idStudent,
        studentName: inv.fullName || "Học sinh",
        studentCode: inv.studentCode || "—",
        nickName: inv.nickName || "",
        className: inv.className || "—",
        parentPhone: inv.parentPhone || "—",
        invoices: [],
      });
    }
    studentGroups.get(key).invoices.push(inv);
  });

  const usedSheetNames = new Set();

  // 2. Tạo Sheet cho từng học sinh
  studentGroups.forEach((student) => {
    // Đặt tên sheet an toàn (tối đa 31 ký tự, không chứa ký tự đặc biệt)
    const cleanStudentName = (student.studentName || "HS").replace(/[/\\?*:[\]]/g, "").trim();
    const cleanCode = (student.studentCode || "").replace(/[/\\?*:[\]]/g, "").trim();
    let baseSheetName = cleanCode ? `${cleanStudentName} (${cleanCode})` : cleanStudentName;
    if (baseSheetName.length > 30) {
      baseSheetName = baseSheetName.substring(0, 30);
    }
    let sheetName = baseSheetName;
    let counter = 1;
    while (usedSheetNames.has(sheetName)) {
      sheetName = `${baseSheetName.substring(0, 26)}_${counter++}`;
    }
    usedSheetNames.add(sheetName);

    const worksheet = workbook.addWorksheet(sheetName, {
      pageSetup: {
        paperSize: 9, // A4
        orientation: "landscape",
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
      },
    });

    const totalInvoiceMoney = student.invoices.reduce((s, i) => s + Number(i.sumMoney || 0), 0);
    const totalRemainingMoney = student.invoices.reduce((s, i) => s + Number(i.afterMoney || 0), 0);

    const numCols = 9;

    // --- TIÊU ĐỀ SHEET VÀ THÔNG TIN HỌC SINH ---
    // Dòng 1: Tiêu đề lớn
    const rTitle = worksheet.addRow([title]);
    rTitle.height = 32;
    rTitle.getCell(1).font = { name: "Times New Roman", bold: true, size: 16, color: { argb: "FF1E3A8A" } };
    rTitle.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

    // Dòng 2: Thông tin học sinh
    const studentInfo = `Họ và tên: ${student.studentName}${student.nickName ? ` (${student.nickName})` : ""}   |   Mã HS: ${student.studentCode}   |   Lớp: ${student.className}   |   SĐT phụ huynh: ${student.parentPhone}`;
    const rStudent = worksheet.addRow([studentInfo]);
    rStudent.height = 22;
    rStudent.getCell(1).font = { name: "Times New Roman", bold: true, size: 12, color: { argb: "FF334155" } };
    rStudent.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

    // Dòng 3: Tổng hợp công nợ của học sinh
    const summaryText = `Tổng số phiếu: ${student.invoices.length}   |   Tổng tiền phải thu: ${formatMoney(totalInvoiceMoney)} đ   |   Công nợ còn lại: ${formatMoney(totalRemainingMoney)} đ   |   Ngày xuất: ${moment().format("DD/MM/YYYY HH:mm")}`;
    const rSummary = worksheet.addRow([summaryText]);
    rSummary.height = 20;
    rSummary.getCell(1).font = { name: "Times New Roman", italic: true, size: 11, color: { argb: "FF64748B" } };
    rSummary.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

    worksheet.addRow([]); // Dòng 4 trống

    worksheet.mergeCells(1, 1, 1, numCols);
    worksheet.mergeCells(2, 1, 2, numCols);
    worksheet.mergeCells(3, 1, 3, numCols);

    // Thiết lập độ rộng các cột
    worksheet.columns = [
      { width: 7 },  // STT
      { width: 32 }, // Tên khoản thu
      { width: 10 }, // Đơn vị
      { width: 10 }, // Số lượng
      { width: 16 }, // Đơn giá
      { width: 11 }, // Thuế (%)
      { width: 14 }, // Giảm giá
      { width: 18 }, // Thành tiền
      { width: 26 }, // Ghi chú
    ];

    // --- TỪNG PHIẾU THU LÀ MỘT KHỐI / BẢNG RIÊNG BIỆT ---
    student.invoices.forEach((inv, invIndex) => {
      const turnStr = inv.turn ? `ĐỢT ${inv.turn}` : "";
      const monthStr = inv.month && inv.year ? `THÁNG ${inv.month}/${inv.year}` : "";
      const scopeStr = typeMap[inv.targetType] || inv.targetType || "Lớp";
      const targetStr = inv.targetName || inv.className || "";
      const statusText = statusMap[inv.status] || inv.status || "—";

      // 1. BANNER TIÊU ĐỀ PHIẾU THU
      const bannerTitle = `PHIẾU THU #${invIndex + 1}: ${inv.invoiceCode || "—"}   |   ${monthStr} ${turnStr}   |   Phạm vi: ${scopeStr} (${targetStr})   |   Trạng thái: ${statusText}`;
      const rBanner = worksheet.addRow([bannerTitle]);
      rBanner.height = 26;
      worksheet.mergeCells(rBanner.number, 1, rBanner.number, numCols);

      for (let c = 1; c <= numCols; c++) {
        const cell = rBanner.getCell(c);
        cell.font = { name: "Times New Roman", bold: true, size: 11.5, color: { argb: "FFFFFFFF" } };
        cell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } }; // Primary Blue
        cell.border = borderThin;
      }

      // 2. DÒNG THÔNG TIN KHOẢNG ÁP DỤNG & ĐIỂM DANH
      const periodStr = `Khoảng áp dụng: ${formatDate(inv.startValidityPeriod)} — ${formatDate(inv.endValidityPeriod)}`;
      let attendStr = "";
      if (inv.arriveT2t6 !== null && inv.arriveT2t6 !== undefined) {
        attendStr = `   |   Điểm danh: Học T2-T6: ${inv.arriveT2t6 || 0}, Học T7: ${inv.arriveT7 || 0}, Nghỉ CP: ${inv.absentCpT2t6 || 0}, Nghỉ KP: ${inv.absentKpT2t6 || 0}, Số ngày: ${inv.quantityDate || 0}`;
      }
      const rMeta = worksheet.addRow([`${periodStr}${attendStr}`]);
      rMeta.height = 20;
      worksheet.mergeCells(rMeta.number, 1, rMeta.number, numCols);

      for (let c = 1; c <= numCols; c++) {
        const cell = rMeta.getCell(c);
        cell.font = { name: "Times New Roman", italic: true, size: 10, color: { argb: "FF334155" } };
        cell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } }; // Light Slate
        cell.border = borderThin;
      }

      // 3. HEADER BẢNG KHOẢN THU CHI TIẾT
      const tableHeaders = [
        "STT",
        "Tên khoản thu",
        "Đơn vị",
        "Số lượng",
        "Đơn giá (đ)",
        "Thuế (%)",
        "Giảm giá",
        "Thành tiền (đ)",
        "Ghi chú",
      ];
      const rHeader = worksheet.addRow(tableHeaders);
      rHeader.height = 24;
      rHeader.eachCell((cell) => {
        cell.font = { name: "Times New Roman", bold: true, size: 10.5, color: { argb: "FF1E293B" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } }; // Slate 200
        cell.border = borderThin;
      });

      // 4. DANH SÁCH CÁC KHOẢN THU TRONG PHIẾU
      const details = Array.isArray(inv.invoiceDetails) && inv.invoiceDetails.length > 0
        ? inv.invoiceDetails
        : [];

      if (details.length > 0) {
        details.forEach((d, dIdx) => {
          let discountText = "0";
          if (d.discount) {
            discountText = d.discountType === "PERCENT"
              ? `${d.discountNumber}%`
              : formatMoney(d.discountNumber);
          }

          const rowValues = [
            dIdx + 1,
            d.revenueName || "Khoản thu",
            d.unit || "—",
            Number(d.quantity || 1),
            Number(d.price || 0),
            d.taxPercentage ? `${d.taxPercentage}%` : "0%",
            discountText,
            Number(d.amount || 0),
            d.description || "",
          ];

          const rData = worksheet.addRow(rowValues);
          rData.height = 22;

          rData.eachCell((cell, colNumber) => {
            cell.font = { name: "Times New Roman", size: 10.5 };
            cell.border = borderThin;

            // STT, Đơn vị, Số lượng, Thuế, Giảm giá (căn giữa)
            if (colNumber === 1 || colNumber === 3 || colNumber === 4 || colNumber === 6 || colNumber === 7) {
              cell.alignment = { horizontal: "center", vertical: "middle" };
            } else if (colNumber === 2 || colNumber === 9) {
              // Tên khoản thu, Ghi chú (căn trái)
              cell.alignment = { horizontal: "left", vertical: "middle" };
            } else {
              // Đơn giá, Thành tiền (căn phải, định dạng tiền tệ)
              cell.alignment = { horizontal: "right", vertical: "middle" };
              if (typeof cell.value === "number") cell.numFmt = "#,##0";
            }
          });
        });
      } else {
        const rEmpty = worksheet.addRow([
          1,
          "Tổng cộng học phí / dịch vụ phiếu thu",
          "Tháng",
          1,
          Number(inv.sumMoney || 0),
          "0%",
          "0",
          Number(inv.sumMoney || 0),
          inv.note || "",
        ]);
        rEmpty.height = 22;
        rEmpty.eachCell((cell, colNumber) => {
          cell.font = { name: "Times New Roman", size: 10.5 };
          cell.border = borderThin;
          if (colNumber === 1 || colNumber === 3 || colNumber === 4 || colNumber === 6 || colNumber === 7) {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          } else if (colNumber === 2 || colNumber === 9) {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          } else {
            cell.alignment = { horizontal: "right", vertical: "middle" };
            if (typeof cell.value === "number") cell.numFmt = "#,##0";
          }
        });
      }

      // 5. DÒNG TỔNG KẾT TÀI CHÍNH CỦA PHIẾU THU
      const rFooter = worksheet.addRow([
        `TỔNG KẾT PHIẾU #${invIndex + 1}:   Phải thu: ${formatMoney(inv.sumMoney)} đ   |   Thiếu/thừa trước: ${formatMoney(inv.beforeMoney)} đ   |   Đã thu: ${formatMoney(inv.collectedMoney)} đ (Tiền mặt: ${formatMoney(inv.cashMoney)} đ, CK: ${formatMoney(inv.transferMoney)} đ)   |   Còn lại: ${formatMoney(inv.afterMoney)} đ`,
      ]);
      rFooter.height = 24;
      worksheet.mergeCells(rFooter.number, 1, rFooter.number, numCols);

      for (let c = 1; c <= numCols; c++) {
        const cell = rFooter.getCell(c);
        cell.font = { name: "Times New Roman", bold: true, size: 10.5, color: { argb: "FF166534" } };
        cell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2EFDA" } }; // Soft Green
        cell.border = borderThin;
      }

      // 6. KHOẢNG CÁCH GIỮA CÁC PHIẾU
      worksheet.addRow([]);
      worksheet.addRow([]);
    });
  });

  // 3. Xuất file
  const buffer = await workbook.xlsx.writeBuffer();
  const timeStamp = moment().format("YYYYMMDD_HHmmss");

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Danh_Sach_Phieu_Thu_Hoc_Sinh_${timeStamp}.xlsx`
  );
}
